import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
export interface SubscriptionStatus {
  status: string; // 'active', 'expired', 'inactive'
  endDate: string | null;
  daysRemaining: number;
  planKey: string | null;
  planName: string | null;
  autoRenewal: boolean;
  isExpiringsSoon: boolean; // true wenn Tage verbleibend <= SUBSC_RENEWAL_WARNING_DAYS
  // Personal Information
  firstName?: string | null;
  lastName?: string | null;
  // Billing Information
  companyName?: string | null;
  street?: string | null;
  postalCode?: string | null;
  city?: string | null;
  country?: string | null;
  // VAT/Tax Information
  isB2B?: boolean;
  customerVAT?: string | null;
  // Customer ID
  customerId?: string | null;
}

export interface SubscriptionPlan {
  planId: number;
  planKey: string;
  name: string;
  durationMonths: number | null;
  durationHours: number | null;
  priceEUR: number;
  discountPercent: number;
  originalPrice: number | null;
  description: string;
  isPopular: boolean;
}

export interface FeatureItem {
  text: string;
  available: boolean;
}

export interface SubscriptionPlanDetail {
  planKey: string;
  name: string;
  priceEUR: number;
  originalPrice?: number;
  discountPercent: number;
  savedAmount?: number;
  durationMonths?: number;
  durationHours?: number;
  description: string;
  detailedDescription: string;
  highlightFeatures: FeatureItem[];
  autoRenew: boolean;
}

export interface PaymentHistory {
  paymentId: number;
  amount: number;
  currency: string;
  provider: string;
  planName: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
  invoiceNumber?: string;
}

export interface UserFeatures {
  canExportPDF: boolean;
  canUseAI: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  userFeatures = signal<UserFeatures>({ canExportPDF: false, canUseAI: false });

  getSubscriptionStatus(): Observable<SubscriptionStatus> {
    return this.http.get<SubscriptionStatus>(`${this.apiUrl}/subscription/status`);
  }

  getAvailablePlans(): Observable<SubscriptionPlan[]> {
    return this.http.get<SubscriptionPlan[]>(`${this.apiUrl}/subscription/plans`);
  }

  activateTrial(): Observable<{ message: string; endDate: string }> {
    return this.http.post<{ message: string; endDate: string }>(
      `${this.apiUrl}/subscription/activate-trial`,
      {}
    );
  }

  extendSubscription(planKey: string, autoRenewal: boolean = false): Observable<{ message: string; endDate: string }> {
    return this.http.post<{ message: string; endDate: string }>(
      `${this.apiUrl}/subscription/extend`,
      { planKey, autoRenewal }
    );
  }

  cancelAutoRenewal(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/subscription/cancel-auto-renewal`,
      {}
    );
  }

  getPlanDetails(planKey: string): Observable<SubscriptionPlanDetail> {
    return this.http.get<SubscriptionPlanDetail>(`${this.apiUrl}/subscription/plan-details/${planKey}`);
  }

  getUserFeatures(): Observable<UserFeatures> {
    return this.http.get<UserFeatures>(`${this.apiUrl}/subscription/user/features`)
      .pipe(
        tap(features => this.userFeatures.set(features))
      );
  }

  hasFeature(feature: 'PDF_EXPORT' | 'AI'): boolean {
    const features = this.userFeatures();
    return feature === 'PDF_EXPORT' ? features.canExportPDF : features.canUseAI;
  }

  getPaymentHistory(): Observable<PaymentHistory[]> {
    return this.http.get<PaymentHistory[]>(`${this.apiUrl}/subscription/payment-history`);
  }

  downloadInvoice(paymentId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/payment/${paymentId}/invoice`, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  downloadXmlInvoice(paymentId: number): Observable<any> {
    return this.http.get(`${this.apiUrl}/payment/${paymentId}/invoice/xml`, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  updateBillingData(billingData: {
    firstName: string;
    lastName: string;
    companyName: string;
    street: string;
    postalCode: string;
    city: string;
    country: string;
    isB2B: boolean;
    customerVAT: string;
  }): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/subscription/update-billing-data`,
      billingData
    );
  }
}
